import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Star, ChevronLeft, ChevronRight, Quote } from 'lucide-react';
const testimonials = [{
  id: 1,
  quote: 'Jenny was absolutely incredible in helping us find our dream home in Summerlin. Her knowledge of the local market and negotiation skills saved us time and money. She truly goes above and beyond.',
  author: 'Sarah & Michael T.',
  transaction: 'Bought in Summerlin'
}, {
  id: 2,
  quote: 'Selling our home in Southern Highlands felt daunting until we met Jenny. She guided us through staging, priced it perfectly, and we had multiple offers within days. A true professional.',
  author: 'David R.',
  transaction: 'Sold in Southern Highlands'
}, {
  id: 3,
  quote: "As out-of-state buyers, we relied heavily on Jenny's expertise. She did video tours for us, pointed out things we wouldn't have noticed, and found us the perfect investment property in Henderson.",
  author: 'The Chen Family',
  transaction: 'Bought in Henderson'
}, {
  id: 4,
  quote: "Jenny's dedication is unmatched. She was always available to answer our questions and made the entire process of buying our first luxury home seamless and stress-free.",
  author: 'Jessica L.',
  transaction: 'Bought in MacDonald Highlands'
}, {
  id: 5,
  quote: "We've worked with many realtors over the years, but Jenny stands out. Her integrity, honesty, and deep understanding of the Las Vegas market make her the only agent we'll ever use.",
  author: 'Robert & Elena M.',
  transaction: 'Sold in Spring Valley'
}];
export function Testimonials() {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [direction, setDirection] = useState(0);
  useEffect(() => {
    const timer = setInterval(() => {
      setDirection(1);
      setCurrentIndex(prev => (prev + 1) % testimonials.length);
    }, 8000);
    return () => clearInterval(timer);
  }, []);
  const slideVariants = {
    enter: (direction: number) => ({
      x: direction > 0 ? 1000 : -1000,
      opacity: 0
    }),
    center: {
      zIndex: 1,
      x: 0,
      opacity: 1
    },
    exit: (direction: number) => ({
      zIndex: 0,
      x: direction < 0 ? 1000 : -1000,
      opacity: 0
    })
  };
  const swipeConfidenceThreshold = 10000;
  const swipePower = (offset: number, velocity: number) => {
    return Math.abs(offset) * velocity;
  };
  const paginate = (newDirection: number) => {
    setDirection(newDirection);
    setCurrentIndex(prev => (prev + newDirection + testimonials.length) % testimonials.length);
  };
  return <section className="py-24 md:py-32 bg-[#FAF9F6] px-6 overflow-hidden" data-id="element-520">
      <div className="max-w-4xl mx-auto text-center" data-id="element-521">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6
      }} className="mb-16" data-id="element-522">
          <span className="text-gold uppercase tracking-[0.2em] text-sm font-medium mb-4 block" data-id="element-523">
            Client Success Stories
          </span>
          <h2 className="text-4xl md:text-5xl font-serif text-charcoal" data-id="element-524">
            What Our <span className="italic text-gold" data-id="element-525">Clients Say</span>
          </h2>
        </motion.div>

        <div className="relative h-[400px] md:h-[300px] flex items-center justify-center" data-id="element-526">
          <AnimatePresence initial={false} custom={direction} data-id="element-527">
            <motion.div key={currentIndex} custom={direction} variants={slideVariants} initial="enter" animate="center" exit="exit" transition={{
            x: {
              type: 'spring',
              stiffness: 300,
              damping: 30
            },
            opacity: {
              duration: 0.2
            }
          }} drag="x" dragConstraints={{
            left: 0,
            right: 0
          }} dragElastic={1} onDragEnd={(e, {
            offset,
            velocity
          }) => {
            const swipe = swipePower(offset.x, velocity.x);
            if (swipe < -swipeConfidenceThreshold) {
              paginate(1);
            } else if (swipe > swipeConfidenceThreshold) {
              paginate(-1);
            }
          }} className="absolute w-full px-4 md:px-16" data-id="element-528">
              <div className="flex justify-center mb-6" data-id="element-529">
                <div className="flex gap-1" data-id="element-530">
                  {[...Array(5)].map((_, i) => <Star key={i} className="w-5 h-5 fill-gold text-gold" data-id="element-531" />)}
                </div>
              </div>

              <div className="relative" data-id="element-532">
                <Quote className="absolute -top-6 -left-4 md:-left-8 w-12 h-12 text-gold/20 rotate-180" data-id="element-533" />
                <p className="text-xl md:text-3xl font-serif text-charcoal leading-relaxed italic mb-8 relative z-10" data-id="element-534">
                  "{testimonials[currentIndex].quote}"
                </p>
              </div>

              <div data-id="element-535">
                <h4 className="text-lg font-medium text-charcoal uppercase tracking-wider" data-id="element-536">
                  {testimonials[currentIndex].author}
                </h4>
                <p className="text-gold text-sm mt-1" data-id="element-537">
                  {testimonials[currentIndex].transaction}
                </p>
              </div>
            </motion.div>
          </AnimatePresence>

          {/* Navigation Buttons */}
          <button className="absolute left-0 top-1/2 -translate-y-1/2 w-12 h-12 flex items-center justify-center rounded-full border border-warmGray-dark text-charcoal-light hover:text-gold hover:border-gold transition-colors z-10 bg-white/50 backdrop-blur-sm hidden md:flex" onClick={() => paginate(-1)} data-id="element-538">
            <ChevronLeft className="w-6 h-6" data-id="element-539" />
          </button>
          <button className="absolute right-0 top-1/2 -translate-y-1/2 w-12 h-12 flex items-center justify-center rounded-full border border-warmGray-dark text-charcoal-light hover:text-gold hover:border-gold transition-colors z-10 bg-white/50 backdrop-blur-sm hidden md:flex" onClick={() => paginate(1)} data-id="element-540">
            <ChevronRight className="w-6 h-6" data-id="element-541" />
          </button>
        </div>

        {/* Dots */}
        <div className="flex justify-center gap-3 mt-8" data-id="element-542">
          {testimonials.map((_, index) => <button key={index} onClick={() => {
          setDirection(index > currentIndex ? 1 : -1);
          setCurrentIndex(index);
        }} className={`w-2.5 h-2.5 rounded-full transition-all duration-300 ${index === currentIndex ? 'bg-gold w-8' : 'bg-warmGray-dark hover:bg-gold/50'}`} data-id="element-543" />)}
        </div>
      </div>
    </section>;
}